import { Application, Container } from 'pixi.js';
import { DESIGN, type RuntimeOptions } from './runtime.js';
import { CameraRig } from './camera.js';
import { FilterStack } from '../fx/filters.js';
import { ParticleEngine } from '../fx/particles.js';
import { GalaxyBackground } from '../world/galaxy.js';

export interface Stage {
  readonly app: Application;
  /** Fixed to the screen, behind everything. Not affected by the camera. */
  readonly background: Container;
  /** Camera-transformed. The globe, the arena, the duel, the ladder. */
  readonly world: Container;
  /** Camera-transformed, above the world. Particles and impacts. */
  readonly effects: Container;
  /** Screen space, above everything. Headlines and full-frame flashes. */
  readonly foreground: Container;
  readonly camera: CameraRig;
  readonly filters: FilterStack;
  readonly particles: ParticleEngine;
  readonly galaxy: GalaxyBackground;
}

/**
 * Build the renderer.
 *
 * WebGL is requested explicitly rather than letting Pixi pick WebGPU: the whole
 * filter chain is authored against the WebGL path, and a broadcast machine is
 * the wrong place to discover a backend difference at runtime.
 *
 * The scene is authored at a fixed 1920x1080 and the root is scaled to fit, so
 * one layout serves a 720p preview, a 1080p stream and a 4K capture, and no
 * position in the codebase depends on the window size.
 */
export async function createStage(
  canvas: HTMLCanvasElement,
  options: RuntimeOptions,
): Promise<Stage> {
  const app = new Application();

  await app.init({
    canvas,
    preference: 'webgl',
    antialias: options.quality.antialias,
    resolution: options.quality.resolution,
    autoDensity: true,
    backgroundColor: 0x03050c,
    powerPreference: 'high-performance',
    resizeTo: window,
    // The overlay is output-only; skipping hit testing saves a full scene
    // traversal per frame with hundreds of cards on screen.
    eventMode: 'none',
    eventFeatures: { move: false, click: false, wheel: false, globalMove: false },
  });

  const root = new Container();
  root.eventMode = 'none';
  app.stage.addChild(root);

  const background = new Container();
  const world = new Container();
  const effects = new Container();
  const foreground = new Container();
  for (const layer of [background, world, effects, foreground]) layer.eventMode = 'none';

  root.addChild(background, world, effects, foreground);

  const galaxy = new GalaxyBackground(options.quality);
  background.addChild(galaxy.view);

  const particles = new ParticleEngine(options.quality);
  effects.addChild(particles.view);

  const camera = new CameraRig(world);
  const filters = new FilterStack(world, options.quality);

  const fit = (): void => {
    const scale = Math.min(window.innerWidth / DESIGN.width, window.innerHeight / DESIGN.height);
    root.scale.set(scale);
    root.position.set(
      (window.innerWidth - DESIGN.width * scale) / 2,
      (window.innerHeight - DESIGN.height * scale) / 2,
    );
  };

  fit();
  window.addEventListener('resize', fit);

  return { app, background, world, effects, foreground, camera, filters, particles, galaxy };
}
